import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { select, Store } from '@ngrx/store';

import { isNull } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { ProfileSearchEnum } from '../../enums/profile-search';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import { SearchableSelectOption } from '../../typings/searchable-select';

interface APNFormGroup {
  county: FormControl<string | null>;
  parcelNumber: FormControl<string | null>;
  state: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search-parcel-number',
  standalone: false,
  templateUrl: './parcel-number.page.html'
})
export class ParcelNumberPageComponent {
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  availableStates$: Observable<SearchableSelectOption[]>;
  availableCounties$: Observable<SearchableSelectOption[]>;

  form: FormGroup<APNFormGroup> = new FormGroup<APNFormGroup>({
    county: new FormControl({ disabled: true, value: null }, Validators.required),
    parcelNumber: new FormControl(null, Validators.required),
    state: new FormControl(null, Validators.required)
  });

  constructor() {
    this.availableStates$ = this.store.pipe(select(fromSettingSelectors.selectAvailableStates));
    this.availableCounties$ = this.store.pipe(select(fromSettingSelectors.selectAvailableCounties));

    this.store
      .pipe(select(fromSettingSelectors.selectSelectedState), distinctUntilChanged())
      .subscribe((selectedState) => {
        if (this.form.controls.state.value !== selectedState) {
          this.form.controls.state.setValue(selectedState, { emitEvent: false });
          this.form.controls.county.markAsUntouched();
          if (isNull(selectedState)) {
            this.form.controls.county.disable();
          } else {
            this.form.controls.county.enable();
          }
        }
      });

    this.form.controls.state.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.store.dispatch(fromSettingActions.updateSelectedState({ payload: { state: value } }));
      if (isNull(value)) {
        this.form.controls.county.disable();
      } else {
        this.form.controls.county.enable();
      }
    });

    this.form.controls.county.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.store.dispatch(fromSettingActions.updateSelectedCounty({ payload: { county: value } }));
    });
  }

  async onSubmit() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const formValues = this.form.getRawValue();
      this.store.dispatch(
        fromPropertyProfileActions.loadProfileByAPN({
          payload: {
            apn: formValues.parcelNumber || '',
            county: formValues.county || '',
            searchType: ProfileSearchEnum.PropertySearch,
            state: formValues.state || ''
          }
        })
      );
    }
  }
}
